import {
  MAX_STARS,
  TASTE_TESTED_SHEF_AVERAGE_RATING_THRESHOLD,
  TASTE_TESTED_SHEF_RATING_COUNT_LOWER_THRESHOLD,
  TASTE_TESTED_SHEF_RATING_COUNT_UPPER_THRESHOLD,
} from './Constants';

/*
 * Stars are based on a scale from 0 to 100. Since the rating scale is based on 5 stars
 * each star is worth 20 or 20%. The utility functions on this file are based on this scale.
 */

/**
 * Converts a rating percentage (0 - 100%) to a stars value (0.00 - 5.00)
 */
export const percentToStars = (percent: number): number => (MAX_STARS * percent) / 100;

/**
 * Converts a stars value (0.00 - 5.00) to a rating percentage (0 - 100%)
 */
export const starsToPercent = (stars: number): number => stars * (100 / MAX_STARS);

/**
 * Converts a stars value (0.00 - 5.00) to a rating score (0.0 - 5.0)
 * Prefer vs .toFixed(1) to account for floating point inconsistencies: https://stackoverflow.com/q/661562/17264481
 * Optionally define a precision. Defaults to 1. Ex: precision of 2 on a rating of 4.6482 will return 4.65
 */
export const starsToRating = (stars: number, precision = 1): number => {
  const factorOfTen = 10 ** precision;
  return Math.round(stars * factorOfTen) / factorOfTen;
};

const FLOATING_POINT_TOLERANCE = 1e-9;

/**
 * Like `starsToRating`, but drops the digits past `precision` instead of rounding. Use it where a rating must
 * not read as a threshold it hasn't reached, such as a band whose top edge is exclusive.
 */
export const truncateStarsToRating = (stars: number, precision = 1): number => {
  const factorOfTen = 10 ** precision;
  return Math.floor(stars * factorOfTen + FLOATING_POINT_TOLERANCE) / factorOfTen;
};

/**
 * Converts a rating percent (0 - 100) to a rating score based on 5 stars (0.0 - 5.0)
 */
export const adjustedStarsToRating = (stars: number, precision = 1): number => {
  const starValue = 20; // Each star is worth 20%
  const avgStarRating = stars / starValue;
  return starsToRating(avgStarRating, precision);
};

export const THUMB_UP_PERCENT = 100;
export const THUMB_MIDDLE_PERCENT = 60;
export const THUMB_DOWN_PERCENT = 20;

export type ThumbUpPercent = typeof THUMB_UP_PERCENT;
export type ThumbMiddlePercent = typeof THUMB_MIDDLE_PERCENT;
export type ThumbDownPercent = typeof THUMB_DOWN_PERCENT;

export type RatingPercent = ThumbUpPercent | ThumbMiddlePercent | ThumbDownPercent;

/** @deprecated Use {@link THUMB_UP_PERCENT}. */
export const POSITIVE_RATING_PERCENT = THUMB_UP_PERCENT;
/** @deprecated Use {@link THUMB_DOWN_PERCENT}. */
export const NEGATIVE_RATING_PERCENT = THUMB_DOWN_PERCENT;
export type PositiveRatingPercent = ThumbUpPercent;
export type NegativeRatingPercent = ThumbDownPercent;

export const percentOfRatings = (count: number, ratingCount: number): number =>
  ratingCount > 0 ? (100 * count) / ratingCount : 0;

export const weightedThumbPercent = (up: number, middle: number, down: number): number => {
  const total = up + middle + down;
  if (total === 0) return 0;
  return (THUMB_UP_PERCENT * up + THUMB_MIDDLE_PERCENT * middle + THUMB_DOWN_PERCENT * down) / total;
};

function ratingCountHash(input: number): number {
  return (Math.abs(input) % 6) + 5;
}

function ratingValueHash(input: number): number {
  return (Math.abs(input) % 6) + 94;
}

export const MEAL_PLAN_DISPLAYABLE_RATING_MIN_COUNT = 6;

/**
 * Whether a dish has enough reviews to surface its real rating. Below this we show a synthetic
 * placeholder (see {@link getMealPlanRating}), so callers wanting a genuine rating must gate on this.
 */
export const hasDisplayableMealPlanRating = (ratingCount: number): boolean =>
  ratingCount >= MEAL_PLAN_DISPLAYABLE_RATING_MIN_COUNT;

export function getMealPlanRating(ratingCount: number, averageRating: number, foodItemId: number): number {
  return hasDisplayableMealPlanRating(ratingCount) ? averageRating : ratingValueHash(foodItemId);
}

export function getMealPlanRatingCount(ratingCount: number, foodItemId: number): number {
  return hasDisplayableMealPlanRating(ratingCount) ? ratingCount : ratingCountHash(foodItemId);
}

export function isTasteTestedShef(ratingCount: number, averageRating: number): boolean {
  return (
    ratingCount < TASTE_TESTED_SHEF_RATING_COUNT_LOWER_THRESHOLD ||
    (ratingCount < TASTE_TESTED_SHEF_RATING_COUNT_UPPER_THRESHOLD &&
      averageRating < TASTE_TESTED_SHEF_AVERAGE_RATING_THRESHOLD)
  );
}

export const TOP_RATED_RATING_COUNT_THRESHOLD = 10;

interface DishWithRating {
  ratingCount: number;
  averageRating: number;
}

/**
 * Comparator for "Top Rated" dish ordering: dishes with > TOP_RATED_RATING_COUNT_THRESHOLD ratings
 * come first, then by averageRating descending, then by ratingCount descending. Shared between the
 * BE "Top Rated" For-You collection and the FE category-carousel TOP_RATED grid.
 */
export const compareDishesByTopRated = (a: DishWithRating, b: DishWithRating): number => {
  const aHasEnoughRatings = a.ratingCount > TOP_RATED_RATING_COUNT_THRESHOLD;
  const bHasEnoughRatings = b.ratingCount > TOP_RATED_RATING_COUNT_THRESHOLD;
  if (aHasEnoughRatings !== bHasEnoughRatings) {
    return aHasEnoughRatings ? -1 : 1;
  }
  if (a.averageRating !== b.averageRating) {
    return a.averageRating > b.averageRating ? -1 : 1;
  }
  return b.ratingCount - a.ratingCount;
};
